import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import API_BASE from '../utils/api';

const token = () => localStorage.getItem('token');
const headers = () => ({ Authorization: `Bearer ${token()}` });

const TERMS = ['Term 1', 'Term 2', 'Half Yearly', 'Annual'];
const defaultAcademicYear = `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`;

// Shared presentational + data-fetching component for a single student's
// progress report — used by the teacher (editable), admin/principal
// (view-any), and parent (view-own-child, read-only) pages.
const ProgressReportCard = ({ studentId, editable = false, accentColor = 'indigo' }) => {
  const [academicYear, setAcademicYear] = useState(defaultAcademicYear);
  const [term, setTerm] = useState(TERMS[0]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [remarksDraft, setRemarksDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const fetchReport = useCallback(async () => {
    if (!studentId) return;
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/progress-reports/${studentId}`, {
        headers: headers(),
        params: { academicYear, term },
      });
      setReport(res.data);
      setRemarksDraft(res.data.remarks || '');
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to load progress report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [studentId, academicYear, term]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const saveRemarks = async () => {
    try {
      setSaving(true);
      await axios.put(`${API_BASE}/progress-reports/${studentId}/remarks`, {
        academicYear, term, remarks: remarksDraft,
      }, { headers: headers() });
      showMessage('success', 'Remarks saved');
      fetchReport();
    } catch (err) {
      showMessage('error', err.response?.data?.error || 'Failed to save remarks');
    } finally {
      setSaving(false);
    }
  };

  const exportPDF = () => {
    if (!report) return;
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Progress Report', 14, 18);
    doc.setFontSize(10);
    doc.text(`${report.student.name}${report.student.rollNumber ? ` (Roll ${report.student.rollNumber})` : ''}`, 14, 25);
    doc.text(`Class: ${report.class ? `${report.class.className} - ${report.class.section}` : '—'}   Period: ${academicYear} · ${term}`, 14, 31);

    autoTable(doc, {
      startY: 38,
      head: [['Subject', 'Exam', 'Marks', 'Grade', 'Result']],
      body: report.subjects.map((s) => [
        s.subject,
        s.examName,
        s.marksObtained != null ? `${s.marksObtained}/${s.totalMarks}` : 'Not entered',
        s.grade || '—',
        s.passed == null ? '—' : (s.passed ? 'Pass' : 'Fail'),
      ]),
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [['Overall %', 'Overall Grade', 'Attendance %']],
      body: [[
        report.overall.percentage != null ? `${report.overall.percentage}%` : '—',
        report.overall.grade || '—',
        report.attendance.percentage != null ? `${report.attendance.percentage}%` : '—',
      ]],
    });

    if (report.remarks) {
      doc.text('Remarks:', 14, doc.lastAutoTable.finalY + 10);
      doc.text(doc.splitTextToSize(report.remarks, 180), 14, doc.lastAutoTable.finalY + 16);
    }

    doc.save(`progress-report-${report.student.name.replace(/\s+/g, '_')}-${term.replace(/\s+/g, '_')}.pdf`);
  };

  if (!studentId) return null;

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      {message.text && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {message.text}
        </div>
      )}

      <div className="flex flex-wrap gap-4 items-end mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Academic Year</label>
          <input value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 w-36" placeholder="2025-2026" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
          <select value={term} onChange={(e) => setTerm(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2">
            {TERMS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        {report && (
          <button onClick={exportPDF} className={`bg-${accentColor}-600 text-white px-4 py-2 rounded-lg hover:bg-${accentColor}-700 transition-colors text-sm font-medium`}>
            📄 Export PDF
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-10 text-gray-500">Loading...</div>
      ) : !report ? (
        <div className="text-center py-10 text-gray-400">No data available</div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6 pb-4 border-b">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">{report.student.name}</h3>
              <p className="text-sm text-gray-500">
                Roll {report.student.rollNumber || '—'} · {report.class ? `${report.class.className} - ${report.class.section}` : 'No class'}
              </p>
            </div>
            <div className="flex gap-3">
              <div className={`bg-${accentColor}-50 border border-${accentColor}-200 rounded-lg px-4 py-2 text-center`}>
                <p className={`text-xl font-bold text-${accentColor}-700`}>{report.overall.percentage != null ? `${report.overall.percentage}%` : '—'}</p>
                <p className={`text-xs text-${accentColor}-600`}>Overall ({report.overall.grade || '—'})</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-center">
                <p className="text-xl font-bold text-blue-700">{report.attendance.percentage != null ? `${report.attendance.percentage}%` : '—'}</p>
                <p className="text-xs text-blue-600">Attendance</p>
              </div>
            </div>
          </div>

          {report.subjects.length === 0 ? (
            <p className="text-center py-6 text-gray-400">No exams recorded for this period</p>
          ) : (
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="p-3 text-sm font-semibold text-gray-600">Subject</th>
                    <th className="p-3 text-sm font-semibold text-gray-600">Exam</th>
                    <th className="p-3 text-sm font-semibold text-gray-600">Marks</th>
                    <th className="p-3 text-sm font-semibold text-gray-600">Grade</th>
                    <th className="p-3 text-sm font-semibold text-gray-600">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {report.subjects.map((s) => (
                    <tr key={s.examId} className="border-b hover:bg-gray-50">
                      <td className="p-3 text-sm font-medium">{s.subject}</td>
                      <td className="p-3 text-sm text-gray-500">{s.examName}</td>
                      <td className="p-3 text-sm">{s.marksObtained != null ? `${s.marksObtained}/${s.totalMarks}` : <span className="text-gray-300">Not entered</span>}</td>
                      <td className="p-3 text-sm">{s.grade || '—'}</td>
                      <td className="p-3 text-sm">
                        {s.passed == null ? '—' : s.passed
                          ? <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">Pass</span>
                          : <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">Fail</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
            {editable ? (
              <>
                <textarea value={remarksDraft} onChange={(e) => setRemarksDraft(e.target.value)}
                  rows="3" placeholder="Add remarks for this student's progress this term..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                <button onClick={saveRemarks} disabled={saving}
                  className={`mt-2 bg-${accentColor}-600 text-white px-5 py-2 rounded-lg hover:bg-${accentColor}-700 transition-colors disabled:opacity-50 text-sm font-medium`}>
                  {saving ? 'Saving...' : 'Save Remarks'}
                </button>
              </>
            ) : (
              <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 min-h-[3rem]">{report.remarks || 'No remarks added yet.'}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ProgressReportCard;
